<template>
  <div id="page-firewood-list">
    <div class="vx-card p-6">
      <div class="flex flex-wrap items-center mb-4">
        <vs-button
          class="sm:mr-4 mr-0"
          color="primary"
          type="filled"
          icon-pack="feather"
          icon="icon-refresh-cw"
          :disabled="listLoading"
          @click="fetchList">
          Refresh
        </vs-button>
        <span v-if="list.length" class="text-sm text-grey mr-4">Auto-refresh every 30s</span>
        <vs-input
          v-if="list.length"
          v-model="searchQuery"
          class="sm:mr-4 mr-0 sm:w-auto w-full"
          placeholder="Search..."
          @input="onSearchInput" />
      </div>
      <div v-if="listLoading" class="text-center p-6">
        <vs-progress indeterminate color="primary" />
        <p class="mt-2">Loading...</p>
      </div>
      <div v-else-if="listError" class="p-4">
        <p class="text-danger">{{ listError }}</p>
      </div>
      <div v-else class="firewood-list-content">
        <p v-if="!list.length" class="p-4 text-grey">No orders pending.</p>
        <template v-else>
          <div class="ag-grid-wrapper">
            <ag-grid-vue
              ref="agGridTable"
              class="ag-theme-material my-4 ag-grid-table"
              :components="gridComponents"
              :columnDefs="gridColumnDefs"
              :defaultColDef="defaultColDef"
              :rowData="list"
              :context="gridContext"
              :floatingFilter="true"
              :pagination="true"
              :paginationPageSize="25"
              :suppressPaginationPanel="true"
              :enableRtl="$vs.rtl"
              @grid-ready="onGridReady"
              @row-clicked="onRowClicked" />
          </div>
          <vs-pagination v-if="gridApi" :total="totalPages" :max="7" v-model="currentPage" />
        </template>
      </div>
    </div>

    <vs-popup
      title="Firewood order details"
      :active.sync="detailPopupActive"
      class="firewood-detail-popup firewood-popup-readonly"
      @close="closeDetail">
      <div v-if="detailLoading" class="firewood-popup-loading">
        <vs-progress indeterminate color="primary" />
        <p class="mt-2">Loading...</p>
      </div>
      <div v-else-if="detailError" class="firewood-popup-error">
        <p class="text-danger">{{ detailError }}</p>
      </div>
      <div v-else-if="detailData" class="firewood-popup-body">
        <div class="firewood-popup-hero">
          <div class="firewood-hero-customer">
            <span class="firewood-hero-label">Customer </span>
            <span class="firewood-hero-name">{{ detailData.customerName || '—' }}</span>
            <span v-if="detailData.cnpOrCui" class="firewood-hero-id firewood-hero-id--prominent"><span class="firewood-hero-id-label">CUI/CNP :</span> {{ detailData.cnpOrCui }}</span>
          </div>
          <div class="firewood-hero-payment">
            <vs-chip color="primary">{{ paymentLabel }}</vs-chip>
          </div>
        </div>

        <div class="firewood-cards-grid">
          <div class="firewood-card">
            <div class="firewood-card-header">
              <feather-icon icon="TruckIcon" svgClasses="w-5 h-5" />
              <span>Vehicle & Driver</span>
            </div>
            <div class="firewood-card-body">
              <div class="firewood-row"><span class="firewood-label">Driver</span><span class="firewood-value">{{ detailData.driver || '—' }}</span></div>
              <div class="firewood-row"><span class="firewood-label">Driver CNP</span><span class="firewood-value">{{ detailData.driverCnp || '—' }}</span></div>
              <div class="firewood-row"><span class="firewood-label">Plate</span><span class="firewood-value">{{ detailData.vehiclePlate || '—' }}</span></div>
              <div class="firewood-row"><span class="firewood-label">Trailer</span><span class="firewood-value">{{ detailData.trailerPlate || '—' }}</span></div>
              <div class="firewood-row"><span class="firewood-label">Odometer</span><span class="firewood-value">{{ detailData.odometer ? detailData.odometer + ' km' : '—' }}</span></div>
            </div>
          </div>
          <div class="firewood-card">
            <div class="firewood-card-header">
              <feather-icon icon="MapPinIcon" svgClasses="w-5 h-5" />
              <span>Addresses</span>
            </div>
            <div class="firewood-card-body">
              <div class="firewood-row"><span class="firewood-label">Billing</span><span class="firewood-value firewood-value-block">{{ detailData.billingAddress || '—' }}</span></div>
              <div class="firewood-row" v-if="detailData.billingStruct">
                <span class="firewood-label">&nbsp;</span>
                <span class="firewood-value firewood-value-block firewood-addr-struct">
                  Localitate <b>{{ detailData.billingStruct.city }}</b> ·
                  Judeţ <b>{{ detailData.billingStruct.county }}</b>
                  <template v-if="detailData.billingStruct.countyCode">(RO-{{ detailData.billingStruct.countyCode }})</template>
                  · places#{{ detailData.billingStruct.placeId }}
                </span>
              </div>
              <div class="firewood-row"><span class="firewood-label">Delivery</span><span class="firewood-value firewood-value-block">{{ detailData.deliveryAddress || '—' }}</span></div>
              <div class="firewood-row" v-if="detailData.deliveryStruct">
                <span class="firewood-label">&nbsp;</span>
                <span class="firewood-value firewood-value-block firewood-addr-struct">
                  Localitate <b>{{ detailData.deliveryStruct.city }}</b> ·
                  Judeţ <b>{{ detailData.deliveryStruct.county }}</b>
                  <template v-if="detailData.deliveryStruct.countyCode">(RO-{{ detailData.deliveryStruct.countyCode }})</template>
                  · places#{{ detailData.deliveryStruct.placeId }}
                </span>
              </div>
              <!-- Pas de bloc structuré = commande saisie en texte libre (borne pas
                   encore à jour). L'import devra deviner la localité, et peut échouer :
                   on le dit ici plutôt que de laisser découvrir le problème à l'envoi ANAF. -->
              <div class="firewood-row" v-if="!detailData.billingStruct">
                <span class="firewood-label">&nbsp;</span>
                <span class="firewood-value firewood-value-block firewood-addr-legacy">
                  Adresse en texte libre (ancienne borne) — la localité sera déduite à l'import, à vérifier avant l'envoi e-factura.
                </span>
              </div>
            </div>
          </div>
          <div class="firewood-card firewood-card-dimensions">
            <div class="firewood-card-header">
              <feather-icon icon="Maximize2Icon" svgClasses="w-5 h-5" />
              <span>Dimensions (m)</span>
            </div>
            <div class="firewood-card-body">
              <div class="firewood-dims-inline">
                <div class="firewood-dim"><span class="firewood-dim-label">L</span><span class="firewood-dim-value">{{ detailData.length || '—' }}</span></div>
                <div class="firewood-dim"><span class="firewood-dim-label">W</span><span class="firewood-dim-value">{{ detailData.width || '—' }}</span></div>
                <div class="firewood-dim"><span class="firewood-dim-label">H</span><span class="firewood-dim-value">{{ detailData.height || '—' }}</span></div>
                <div class="firewood-dim firewood-dim--result">
                  <span class="firewood-dim-label">=</span>
                  <span class="firewood-dim-value">{{ detailData.totals.totalVolume || '—' }} <abbr title="Stere — geometric volume L×W×H">mst</abbr></span>
                </div>
              </div>
              <div class="firewood-volume-row">
                <span class="firewood-label">× 0.5 → m³ solid wood</span>
                <span class="firewood-scale-pill firewood-scale-pill--solid">
                  <span class="firewood-label">m³ solid wood</span> {{ detailData.totals.totalVolume*0.5 }} m³
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="firewood-scale-badge">
          <div class="firewood-scale-columns">
            <div class="firewood-scale-col">
              <div class="firewood-scale-col-header">
                <div class="firewood-scale-icon-wrap">
                  <feather-icon icon="ActivityIcon" svgClasses="w-7 h-7" />
                </div>
                <span class="firewood-scale-label">Scale</span>
              </div>
              <span class="firewood-scale-value firewood-scale-value-right">{{ displayTotalTonnage }}</span>
            </div>
            <div class="firewood-scale-col-divider" aria-hidden="true"></div>
            <div class="firewood-scale-col">
              <div class="firewood-scale-col-header">
                <span class="firewood-scale-label">Density Check</span>
              </div>
              <span class="firewood-scale-value firewood-scale-value-right">{{ densityCheck }}</span>
            </div>
          </div>
        </div>

        <div class="firewood-products-section" v-if="detailData.products && detailData.products.length">
          <div class="firewood-section-title">
            <feather-icon icon="PackageIcon" svgClasses="w-5 h-5" />
            <span>Products</span>
          </div>
          <div class="firewood-table-card">
            <table class="firewood-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Tonnage</th>
                  <th>Volume</th>
                  <th>Price/ton (HTVA)</th>
                  <th>Amount HTVA</th>
                  <th>Unit</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, i) in detailData.products" :key="i">
                  <td><strong>{{ p.name || '—' }}</strong></td>
                  <td>{{ p.tonnage || '—' }}</td>
                  <td>{{ p.volume ? (parseFloat(p.volume) * 0.5).toFixed(2) : '—' }}</td>
                  <td>{{ p.pricePerTon || '—' }}</td>
                  <td>{{ p.amount || '—' }}</td>
                  <td>RON</td>
                </tr>
                <!-- Remise POS annoncée par la borne : deviendra une ligne
                     « discount » distincte sur la facture. -->
                <tr v-if="detailData.discount" class="firewood-row-discount">
                  <td colspan="4"><strong>POS discount (−{{ detailData.discount.rate }}%)</strong></td>
                  <td>−{{ detailData.discount.amount }}</td>
                  <td>RON</td>
                </tr>
                <tr v-if="detailData.discount && detailData.totals" class="firewood-row-net">
                  <td colspan="4"><strong>Total HTVA after discount</strong></td>
                  <td><strong>{{ detailData.totals.subtotalHTVA || '—' }}</strong></td>
                  <td>RON</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="firewood-popup-footer">
          <vs-button v-if="detailData.status !== 'imported' && !readOnlyRole" color="primary" size="large" icon="icon-arrow-right" icon-pack="feather" @click="transformToInvoice">Convert to invoice</vs-button>
        </div>
      </div>
    </vs-popup>

    <vs-popup
      title="Transformation en cours"
      :active.sync="transformPopupActive"
      class="firewood-transform-popup"
      @close="closeTransformPopup">
      <div class="firewood-transform-steps">
        <div
          v-for="(label, idx) in transformStepLabels"
          :key="idx"
          class="firewood-transform-step"
          :class="'firewood-step-' + (transformStepStatus[idx] || 'pending')">
          <span class="firewood-step-num">{{ idx + 1 }}.</span>
          <span class="firewood-step-label">{{ label }}</span>
          <span v-if="transformStepStatus[idx] === 'running'" class="firewood-step-icon"><vs-progress indeterminate size="small" /></span>
          <span v-else-if="transformStepStatus[idx] === 'ok'" class="firewood-step-icon firewood-step-ok">✓</span>
          <span v-else-if="transformStepStatus[idx] === 'error'" class="firewood-step-icon firewood-step-error">✗</span>
        </div>
      </div>
      <div v-if="transformError" class="firewood-transform-error">
        {{ transformError }}
      </div>
    </vs-popup>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import axios from "@/axios.js";
import { isAccountantOnly } from "@/helpers/roleAccess.js";
import CellRendererView from "./cell-renderer/CellRendererView.vue";
import CellRendererDate from "./cell-renderer/CellRendererDate.vue";

const PAYMENT_OPTIONS = [
  { value: "cash", label: "Cash / Numerar", icon: "💵" },
  { value: "pos", label: "POS / Card", icon: "💳" },
  { value: "op", label: "OP / Ordin de plată", icon: "📄" },
];

export default {
  name: "FirewoodList",
  components: { AgGridVue, CellRendererView, CellRendererDate },
  data() {
    return {
      // Comptable : consultation seulement (pas de conversion en facture).
      readOnlyRole: isAccountantOnly(),
      gridComponents: { CellRendererView, CellRendererDate },
      list: [],
      listLoading: false,
      listError: null,
      searchQuery: "",
      gridApi: null,
      detailPopupActive: false,
      detailLoading: false,
      detailError: null,
      detailData: null,
      detailId: null,
      paymentOptions: PAYMENT_OPTIONS,
      transformPopupActive: false,
      transformStepStatus: [],
      transformError: null,
      transformStepLabels: [
        "Fetching customer (CUI/CNP lookup)",
        "Search by name in database",
        "If not found: create customer (name + CUI, no address)",
        "Create operation invoice (idoperationcategory 2) + copy wood_order_queues.xml → operations.xml_macdo",
        "Fill fields from XML (driver, vehicle, products, totals, etc.)",
      ],
      defaultColDef: {
        sortable: true,
        resizable: true,
        suppressMenu: true,
      },
      columnDefs: null,
      refreshIntervalId: null,
    };
  },
  computed: {
    gridColumnDefs() {
      return [
        { headerName: "", field: "id", flex: 1, filter: true, width: 120, valueGetter: (p) => (p.data ? "SC-" + p.data.id : "") },
        { headerName: "Date",  width: 280,field: "createdAt", flex: 1, valueGetter: (p) => (p.data && p.data.createdAt ? p.data.createdAt : null), cellRendererFramework: "CellRendererDate" },
        { headerName: "Plate",  width: 180, field: "plaque", flex: 1, filter: true },
        { headerName: "Customer", width: 400, field: "client", flex: 1, filter: true },
        { headerName: "Weight (t)", width: 150, field: "poids", flex: 1, filter: true, type: "numericColumn" },
        // volume arrive en stères (mst) depuis le XML → × 0.5 (coef de cubage) pour l'afficher en m³ bois solide
        { headerName: "Volume (m³)", width: 150, field: "volume", flex: 1, filter: true, type: "numericColumn", valueGetter: (p) => (p.data && p.data.volume ? Number((parseFloat(p.data.volume) * 0.5).toFixed(2)) : null), valueFormatter: (p) => (p.value != null ? p.value.toFixed(2) : "—") },
        { headerName: "RON (TTC)", width: 150, field: "prix", flex: 1, filter: true, type: "numericColumn", valueFormatter: (p) => (p.value != null && p.value !== "" ? Number(p.value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—") },
        { headerName: "", cellRendererFramework: "CellRendererView", width: 72, maxWidth: 72, sortable: false, filter: false },
      ];
    },
    gridContext() {
      return { openDetail: this.openDetail };
    },
    totalPages() {
      return this.gridApi ? this.gridApi.paginationGetTotalPages() : 0;
    },
    currentPage: {
      get() {
        return this.gridApi ? this.gridApi.paginationGetCurrentPage() + 1 : 1;
      },
      set(val) {
        if (this.gridApi) this.gridApi.paginationGoToPage(val - 1);
      },
    },
    paymentLabel() {
      if (!this.detailData || !this.detailData.paymentType) return "—";
      const pt = (this.detailData.paymentType || "").toLowerCase();
      const opt = PAYMENT_OPTIONS.find((o) => o.value === pt);
      return opt ? opt.icon + " " + opt.label : this.detailData.paymentType;
    },
    // Volume comes from the XML only (no calculation here).
    // 1) If the sender filled <Totals><TotalVolume>...</TotalVolume>, we use that.
    // 2) Else we sum each <Product volume="..."> from the XML.
    // The real volume calculation (e.g. from scale/weighbridge) is done by whoever produces the XML (ScreenWoodOrder / scale system).
    displayTotalVolume() {
      if (!this.detailData) return "—";
      if (this.detailData.totals && this.detailData.totals.totalVolume != null && this.detailData.totals.totalVolume !== "") {
        return this.detailData.totals.totalVolume + " m³";
      }
      if (this.detailData.products && this.detailData.products.length) {
        const sum = this.detailData.products.reduce((acc, p) => acc + (parseFloat(p.volume) || 0), 0);
        return sum ? sum.toFixed(2) + " m³" : "—";
      }
      return "—";
    },
    displayTotalTonnage() {
      if (!this.detailData) return "—";
      if (this.detailData.totals && this.detailData.totals.totalTonnage != null && this.detailData.totals.totalTonnage !== "") {
        return this.detailData.totals.totalTonnage + " t";
      }
      if (this.detailData.products && this.detailData.products.length) {
        const sum = this.detailData.products.reduce((acc, p) => acc + (parseFloat(p.tonnage) || 0), 0);
        return sum ? sum.toFixed(2) + " t" : "—";
      }
      return "—";
    },
    // Density Check = totalTonnage / (totalVolume * 0.5) → kg/m³ (tonnage en t → *1000)
    densityCheck() {
      if (!this.detailData || !this.detailData.totals) return "—";
      const t = parseFloat(this.detailData.totals.totalTonnage);
      const v = parseFloat(this.detailData.totals.totalVolume);
      if (isNaN(t) || isNaN(v) || v === 0) return "—";
      const vol = v * 0.5;
      if (vol === 0) return "—";
      const densityKgM3 = (t * 1000) / vol;
      return densityKgM3.toFixed(1) + " kg/m³";
    },
  },
  created() {
    this.fetchList();
    this.refreshIntervalId = setInterval(() => this.fetchList(), 30000);
  },
  beforeDestroy() {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
  },
  methods: {
    onGridReady(params) {
      this.gridApi = params.api;
    },
    onSearchInput() {
      if (this.gridApi) this.gridApi.setQuickFilter(this.searchQuery || "");
    },
    onRowClicked(params) {
      if (params.data && params.data.id) this.openDetail(params.data.id);
    },
    fetchList() {
      this.listLoading = true;
      this.listError = null;
      axios
        .get("/wood-order-queue")
        .then((res) => {
          const raw = res.data || [];
          const twoDaysMs = 2 * 24 * 60 * 60 * 1000;
          const now = Date.now();
          this.list = raw.filter((row) => {
            if (!row.createdAt) return false;
            const created = new Date(row.createdAt).getTime();
            return now - created <= twoDaysMs;
          });
          this.listLoading = false;
        })
        .catch((err) => {
          this.listError = (err.response && err.response.data && err.response.data.error) || err.message || "Load error";
          this.list = [];
          this.listLoading = false;
        });
    },
    formatDate(createdAt) {
      if (!createdAt) return "";
      const d = new Date(createdAt);
      return d.toLocaleDateString("en-GB", { dateStyle: "short" }) + " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    },
    getRelativeTime(value) {
      if (!value) return "";
      const d = new Date(value);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return diffMins + " min ago";
      if (diffHours < 24) return diffHours + (diffHours === 1 ? " hour ago" : " hours ago");
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return diffDays + " days ago";
      if (diffDays < 30) return Math.floor(diffDays / 7) + (diffDays < 14 ? " week ago" : " weeks ago");
      return "";
    },
    dateCellRenderer(params) {
      const v = params.value != null ? params.value : (params.data && params.data.createdAt);
      if (v == null || v === "") return "—";
      const d = new Date(v);
      const dateStr = d.toLocaleDateString("en-GB", { dateStyle: "short" }) + " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
      const rel = this.getRelativeTime(v);
      const relText = rel ? " · " + rel : "";
      return '<div class="firewood-date-wrap"><span class="firewood-date-value">' + this.escapeHtml(dateStr) + "</span>" + (rel ? '<span class="firewood-date-bubble">' + this.escapeHtml(rel) + "</span>" : "") + "</div>";
    },
    escapeHtml(str) {
      if (str == null) return "";
      const div = document.createElement("div");
      div.textContent = str;
      return div.innerHTML;
    },
    openDetail(id) {
      this.detailId = id;
      this.detailPopupActive = true;
      this.detailError = null;
      this.detailData = null;
      this.detailLoading = true;
      axios
        .get("/wood-order-queue/" + id)
        .then((res) => {
          this.detailData = this.parseWoodOrderXml(res.data.xml);
          if (this.detailData) {
            this.detailData.id = res.data.id;
            this.detailData.status = res.data.status;
            this.detailData.rawXml = res.data.xml;
            const pt = (this.detailData.paymentType || "").toLowerCase();
            if (["cash", "pos", "op"].indexOf(pt) === -1) this.detailData.paymentType = "cash";
            else this.detailData.paymentType = pt;
          }
          this.detailLoading = false;
        })
        .catch((err) => {
          this.detailError = (err.response && err.response.data && err.response.data.error) || err.message || "Load error";
          this.detailLoading = false;
        });
    },
    closeDetail() {
      this.detailId = null;
      this.detailData = null;
      this.detailError = null;
    },
    /**
     * Adresse structurée portée par les attributs de <Billing>/<Delivery>.
     * Miroir front de addressStructFromNode (woodOrderQueue.controller.js).
     *
     * Renvoie null quand l'élément ne porte que du texte : c'est le signal que
     * l'import devra deviner la localité, ce que le détail affiche explicitement.
     */
    addressStruct(el) {
      if (!el || !el.getAttribute) return null;
      const attr = (name) => (el.getAttribute(name) || "").trim();
      const struct = {
        placeId: attr("placeId"),
        postalCode: attr("postalCode"),
        city: attr("city"),
        county: attr("county"),
        countyCode: attr("countyCode"),
        street: attr("street"),
        number: attr("number"),
        details: attr("details"),
        country: attr("country") || "RO",
      };
      const hasContent = !!(struct.placeId || struct.postalCode || struct.city || struct.street || struct.number);
      return hasContent ? struct : null;
    },
    /**
     * Remise POS portée par <Discount type rate base amount/> (montants HT).
     * Miroir front de discountFromNode (helpers/firewoodDiscount.js) : null quand
     * l'élément est absent ou sans montant, donc pour toute commande sans remise.
     */
    kioskDiscount(el) {
      if (!el || !el.getAttribute) return null;
      const amount = parseFloat(el.getAttribute("amount"));
      if (!isFinite(amount) || amount <= 0) return null;
      return {
        type: (el.getAttribute("type") || "").trim().toLowerCase(),
        rate: el.getAttribute("rate") || "",
        amount: amount.toFixed(2),
      };
    },
    parseWoodOrderXml(xmlString) {
      if (!xmlString || typeof xmlString !== "string") return null;
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xmlString.trim(), "text/xml");
        const getText = (el) => (el && el.textContent ? el.textContent.trim() : "");
        const getAttr = (el, name) => (el && el.getAttribute ? el.getAttribute(name) : "");

        const customer = doc.querySelector("Customer");
        const vehicle = doc.querySelector("Vehicle");
        const addresses = doc.querySelector("Addresses");
        const dimensions = doc.querySelector("Dimensions");
        const productsEl = doc.querySelector("Products");
        const totals = doc.querySelector("Totals");

        const products = [];
        if (productsEl) {
          const nodes = productsEl.querySelectorAll("Product");
          nodes.forEach((n) => {
            products.push({
              id: getAttr(n, "id"),
              name: getAttr(n, "name") || getText(n),
              tonnage: getAttr(n, "tonnage"),
              volume: getAttr(n, "volume"),
              pricePerTon: getAttr(n, "pricePerTon"),
              amount: getAttr(n, "amount"),
              unit: getAttr(n, "unit"),
            });
          });
        }

        let totalsObj = null;
        if (totals) {
          totalsObj = {
            totalTonnage: getText(totals.querySelector("TotalTonnage")),
            totalVolume: getText(totals.querySelector("TotalVolume")),
            subtotalHTVA: getText(totals.querySelector("SubtotalHTVA")),
            vatRate: getText(totals.querySelector("VatRate")),
            vatAmount: getText(totals.querySelector("VatAmount")),
            totalTTC: getText(totals.querySelector("TotalTTC")),
          };
        }

        const driverEl = doc.querySelector("Driver");
        const driverCnp =
          (driverEl && getAttr(driverEl, "cnp")) ||
          getText(doc.querySelector("DriverCnp")) ||
          (driverEl && getText(driverEl.querySelector("Cnp"))) ||
          "";

        return {
          customerName: customer ? getText(customer.querySelector("Name")) : "",
          cnpOrCui: customer ? getText(customer.querySelector("CnpOrCui")) : "",
          driver: getText(driverEl),
          driverCnp,
          vehiclePlate: vehicle ? getText(vehicle.querySelector("Plate")) : "",
          trailerPlate: vehicle ? getText(vehicle.querySelector("TrailerPlate")) : "",
          odometer: vehicle ? getText(vehicle.querySelector("Odometer")) : "",
          billingAddress: addresses ? getText(addresses.querySelector("Billing")) : "",
          deliveryAddress: addresses ? getText(addresses.querySelector("Delivery")) : "",
          // Champs séparés portés par les attributs (borne à jour). null quand la
          // commande n'a qu'une ligne de texte libre — voir addressStruct().
          billingStruct: addresses ? this.addressStruct(addresses.querySelector("Billing")) : null,
          deliveryStruct: addresses ? this.addressStruct(addresses.querySelector("Delivery")) : null,
          length: dimensions ? getText(dimensions.querySelector("Length")) : "",
          width: dimensions ? getText(dimensions.querySelector("Width")) : "",
          height: dimensions ? getText(dimensions.querySelector("Height")) : "",
          paymentType: getText(doc.querySelector("PaymentType")),
          discount: this.kioskDiscount(doc.querySelector("Discount")),
          products,
          totals: totalsObj,
        };
      } catch (e) {
        console.error("parseWoodOrderXml error:", e);
        return null;
      }
    },
    closeTransformPopup() {
      this.transformStepStatus = [];
      this.transformError = null;
    },
    async transformToInvoice() {
      if (!this.detailData || !this.detailData.id) {
        this.$vs.notify({ title: "Error", text: "No order selected.", color: "danger" });
        return;
      }
      const rawXml = this.detailData.rawXml;
      if (!rawXml || typeof rawXml !== "string") {
        this.$vs.notify({ title: "Error", text: "XML data missing. Re-open the order detail.", color: "danger" });
        return;
      }
      this.transformPopupActive = true;
      this.transformError = null;
      this.transformStepStatus = ["running", "pending", "pending", "pending", "pending"];
      const queueId = this.detailData.id;
      try {
        const response = await axios.post("/wood-order-queue/" + queueId + "/transform-to-invoice", { xml: rawXml });
        // L'adresse de la borne est saisie en texte libre : l'API la rapproche de
        // la table des localités. Quand elle n'y arrive pas, la facture est bien
        // créée mais SANS adresse — et l'envoi e-factura sera refusé plus tard.
        // On le dit maintenant plutôt que d'afficher un succès net. Les mêmes
        // warnings portent aussi les écarts de base HT et de remise POS.
        const warnings = (response.data && response.data.warnings) || [];
        this.transformStepStatus = ["ok", "ok", "ok", "ok", warnings.length ? "error" : "ok"];
        if (this.detailData) this.detailData.status = "imported";
        if (warnings.length) {
          this.$vs.notify({
            color: "warning",
            title: "Invoice created — please check",
            text: warnings.join(" "),
            time: 12000,
          });
        } else {
          this.$vs.notify({
            color: "success",
            title: "Invoice Created!",
            text: "The invoice was successfully generated",
          });
        }
        this.transformPopupActive = false;
        this.detailPopupActive = false;
        await this.$nextTick();
        await new Promise((r) => setTimeout(r, 100));
        this.$router.push({ path: "/firewood-invoice/" + response.data.id + "?fromEdit=true" });
        this.fetchList();
      } catch (err) {
        const msg = (err.response && err.response.data && (err.response.data.message || err.response.data.error)) || err.message || "Transform failed";
        this.transformError = msg;
        const done = this.transformStepStatus.indexOf("running");
        if (done >= 0) {
          for (let i = 0; i < 5; i++) this.transformStepStatus[i] = i < done ? "ok" : i === done ? "error" : "pending";
        }
        this.$vs.notify({
          color: "warning",
          title: "Transformation failed",
          text: msg,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#page-firewood-list {
  .firewood-list-content {
    width: 100%;
  }
  .firewood-hero-id--prominent {
    font-size: 1.35rem;
    background: rgba(var(--vs-primary), 0.18);
    border-color: rgba(var(--vs-primary), 0.5);
    color: #0f172a;
  }
  .ag-grid-wrapper {
    width: 100%;
    min-height: 420px;
    height: calc(var(--vh, 1vh) * 100 - 30rem);
  }
  @media screen and (max-height: 800px) {
    .ag-grid-wrapper {
      height: 620px;
    }
  }
  .ag-grid-wrapper .ag-grid-table {
    width: 100% !important;
    height: 100% !important;
  }
}
</style>

<style lang="scss">
/* Date column cell (bubble) */
.firewood-date-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.firewood-date-value {
  white-space: nowrap;
}
.firewood-date-bubble {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--vs-primary);
  background: rgba(var(--vs-primary), 0.12);
  border-radius: 999px;
  white-space: nowrap;
}

/* ========== Firewood detail popup — design ========== */
.firewood-detail-popup.firewood-popup-readonly {
  .vs-popup {
    max-width: 90vw;
    width: 920px;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  }
  .vs-popup--header {
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  }
  .vs-popup--content {
    padding: 0;
    max-height: 85vh;
    overflow-y: auto;
    background: #fafbfc;
  }
}

.firewood-popup-loading,
.firewood-popup-error {
  padding: 3rem 2rem;
  text-align: center;
}

.firewood-popup-body {
  padding: 0;
}

/* Hero: customer + payment */
.firewood-popup-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1.5rem 1.75rem;
  background: linear-gradient(135deg, rgba(var(--vs-primary), 0.08) 0%, rgba(var(--vs-primary), 0.03) 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.firewood-hero-customer {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.firewood-hero-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(var(--vs-primary), 0.9);
}
.firewood-hero-name {
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
}
.firewood-hero-id {
  display: inline-block;
  margin-top: 0.35rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.08em;
  background: rgba(var(--vs-primary), 0.1);
  border: 1px solid rgba(var(--vs-primary), 0.25);
  border-radius: 6px;
  padding: 0.2rem 0.6rem;
  font-family: monospace;
}
.firewood-hero-id-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(var(--vs-primary), 0.85);
  font-family: inherit;
  opacity: 0.8;
}
.firewood-hero-payment .vs-chip {
  font-weight: 600;
  font-size: 0.85rem;
}

/* Transform popup */
.firewood-transform-popup .vs-popup--content {
  padding: 1.5rem;
}
.firewood-transform-steps {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.firewood-transform-step {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  &:last-child {
    border-bottom: none;
  }
}
.firewood-step-num {
  font-weight: 700;
  min-width: 1.5rem;
  color: #64748b;
}
.firewood-step-label {
  flex: 1;
  font-size: 0.9rem;
}
.firewood-step-icon {
  font-size: 1.1rem;
  &.firewood-step-ok {
    color: #22c55e;
  }
  &.firewood-step-error {
    color: #ef4444;
  }
}
.firewood-step-pending .firewood-step-num {
  color: #94a3b8;
}
.firewood-step-running .firewood-step-num {
  color: var(--vs-primary);
}
.firewood-transform-error {
  margin-top: 1rem;
  padding: 0.75rem;
  background: rgba(239, 68, 68, 0.1);
  color: #b91c1c;
  border-radius: 8px;
  font-size: 0.9rem;
}

/* Cards grid */
.firewood-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.25rem;
  padding: 1.5rem 1.75rem;
}

.firewood-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

.firewood-card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(var(--vs-primary), 0.95);
  background: linear-gradient(180deg, rgba(var(--vs-primary), 0.06) 0%, transparent 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  svg {
    opacity: 0.9;
  }
}

.firewood-card-body {
  padding: 1rem;
}

.firewood-row {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  &:first-child {
    padding-top: 0;
  }
}
.firewood-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center; /* centre verticalement */
}
.firewood-value {
  font-size: 0.95rem;
  font-weight: 500;
  color: #1e293b;
  &.firewood-value-block {
    line-height: 1.45;
    word-break: break-word;
  }
}

/* Ligne « champs séparés » sous une adresse : c'est une preuve de traçabilité
   (quelle localité de `places` a été choisie à la borne), pas une donnée que
   l'opérateur doit lire à chaque fois — d'où le gris et la petite taille. */
.firewood-addr-struct {
  font-size: 0.8rem;
  color: #64748b;
  b { color: #334155; font-weight: 600; }
}
/* Adresse en texte libre : l'import devra deviner, ça mérite l'ambre. */
.firewood-addr-legacy {
  font-size: 0.8rem;
  color: #b45309;
}

.firewood-dims-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f1f5f9;
}
.firewood-dim {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
}
.firewood-dim-label {
  font-size: 0.7rem;
  font-weight: 700;
  color: #94a3b8;
}
.firewood-dim-value {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

.firewood-volume-row {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.firewood-volume-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: rgba(var(--vs-primary), 0.95);
}

/* Scale badge — live from weighbridge */
.firewood-scale-badge {
  margin: 0 1.75rem 1.5rem;
  border-radius: 14px;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(var(--vs-primary), 0.15) 0%, rgba(var(--vs-primary), 0.06) 50%, rgba(34, 197, 94, 0.08) 100%);
  border: 1px solid rgba(var(--vs-primary), 0.25);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5);
  position: relative;
}
.firewood-scale-columns {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: stretch;
  padding: 1.25rem 1.5rem;
  position: relative;
}
.firewood-scale-col {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-width: 0;
}
.firewood-scale-col-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
}
.firewood-scale-value-right {
  margin-left: auto;
}
.firewood-scale-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.firewood-scale-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(145deg, rgba(var(--vs-primary), 0.2) 0%, rgba(var(--vs-primary), 0.08) 100%);
  border: 1px solid rgba(var(--vs-primary), 0.2);
  color: rgba(var(--vs-primary), 0.95);
  flex-shrink: 0;
}
.firewood-scale-value {
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  background: linear-gradient(180deg, rgba(var(--vs-primary), 0.2) 0%, rgba(var(--vs-primary), 0.1) 100%);
  color: rgba(var(--vs-primary), 1);
  border: 1px solid rgba(var(--vs-primary), 0.3);
  width: fit-content;
  min-height: 2.5rem;
  line-height: 1.4;
  display: inline-flex;
  align-items: center;
}
.firewood-scale-col-divider {
  width: 1px;
  background: rgba(var(--vs-primary), 0.2);
  margin: 0 0.5rem;
  align-self: stretch;
}
.firewood-scale-text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex: 1;
}
.firewood-scale-title {
  font-size: 1.05rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
}
.firewood-scale-sub {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
}
.firewood-scale-pill {
  font-size: 1.65rem;
  font-weight: 800;
  letter-spacing: 0.15em;
  padding: 0.35rem 0.65rem;
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(var(--vs-primary), 0.25) 0%, rgba(var(--vs-primary), 0.15) 100%);
  color: rgba(var(--vs-primary), 1);
  border: 1px solid rgba(var(--vs-primary), 0.35);
  flex-shrink: 0;
}

/* Products section */
.firewood-products-section {
  padding: 0 1.75rem 1.5rem;
}

.firewood-section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(var(--vs-primary), 0.95);
  margin-bottom: 0.75rem;
  padding-left: 0.25rem;
}

.firewood-table-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.firewood-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  th {
    text-align: left;
    padding: 0.75rem 1rem;
    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
    font-weight: 700;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #475569;
    border-bottom: 2px solid rgba(var(--vs-primary), 0.2);
  }
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
  }
  tbody tr {
    transition: background 0.15s ease;
    &:last-child td {
      border-bottom: none;
    }
    &:hover {
      background: rgba(var(--vs-primary), 0.04);
    }
  }
  /* Remise POS : montant négatif, à ne pas confondre avec un produit. */
  tr.firewood-row-discount td {
    color: rgba(var(--vs-warning), 1);
  }
  tr.firewood-row-net td {
    background: #f8fafc;
  }
}

/* Footer */
.firewood-popup-footer {
  padding: 1.25rem 1.75rem;
  background: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: flex-end;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.03);
}
</style>
